import { useEffect, useState } from "react";
import { imageVariant } from "@/lib/imageVariant";
import { logoImage } from "@/lib/projectImages";
import { cn } from "@/lib/utils";

/**
 * PropPay's LetterAvatar, ported (Andrew, 2026-09-27: "use the same user image
 * library and structure as PropPay and remove the ability for users to upload
 * their own pics").
 *
 * A logo drawn on NOTHING (a transparent PNG) gets a plate chosen for its ink —
 * see `plateClass`.
 *
 * PEOPLE are initials on a tint chosen from their NAME — the same person is
 * the same colour everywhere, on every device, with nothing uploaded. There is
 * no photo path for a person any more. LOGOS (a project's, a company's) are
 * drawn on a white disc, sized to sit inside the circle — or edge to edge when the
 * artwork is itself a full-bleed square — and a logo that fails to load falls
 * back to initials in the logo's own hue.
 *
 * The tints are PropPay's deep-jewel set turned cooler for SaleFish: the warm
 * ochre / burgundy / maroon / copper are swapped for violet, plum, cobalt and
 * teal. Each clears 4.5:1 fg-on-bg in both themes (same lightness steps as the
 * PropPay set they replace).
 */
const TINTS: Tint[] = [
  { fg: "158 50% 25%", bg: "155 32% 86%", dfg: "155 40% 64%", dbg: "156 26% 20%" }, // forest
  { fg: "214 60% 34%", bg: "213 46% 89%", dfg: "212 58% 74%", dbg: "213 30% 23%" }, // navy
  { fg: "191 52% 26%", bg: "190 34% 87%", dfg: "188 44% 64%", dbg: "190 30% 20%" }, // petrol
  { fg: "236 40% 43%", bg: "235 40% 92%", dfg: "234 55% 78%", dbg: "235 24% 25%" }, // indigo
  { fg: "262 44% 40%", bg: "262 44% 91%", dfg: "262 60% 78%", dbg: "262 28% 24%" }, // violet
  { fg: "292 36% 34%", bg: "292 30% 90%", dfg: "292 44% 76%", dbg: "292 22% 22%" }, // plum
  { fg: "222 62% 38%", bg: "222 60% 91%", dfg: "222 70% 76%", dbg: "222 34% 24%" }, // cobalt
  { fg: "174 50% 24%", bg: "174 34% 86%", dfg: "174 40% 62%", dbg: "174 28% 19%" }, // teal
];

export function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h << 5) - h + s.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

export function initials(name: string | null | undefined): string {
  // Letters and digits only, so an avatar never shows "(" or "-". A record
  // with no name yet (a contact mid-import) is "?", never a crash.
  const parts = (name ?? "")
    .trim()
    .split(/\s+/)
    .map((p) => p.replace(/[^\p{L}\p{N}]/gu, ""))
    .filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

type Tint = { fg: string; bg: string; dfg: string; dbg: string };

/**
 * What one look at a logo tells us: its brand hue, and whether it is a
 * full-bleed tile (see `isFullBleed`).
 */
type Probe = {
  tint: Tint | null;
  bleed: boolean;
  /** Drawn on nothing — a PNG with no background (see `readTransparency`). */
  transparent?: boolean;
  /** How light the logo's own ink is, 0 (black) to 1 (white). Null when unread. */
  inkLightness?: number | null;
  /**
   * The share of the ink, weighted by coverage, that is DARK (lightness ≤ 0.35)
   * — the part a dark plate would swallow. See `plateClass`.
   */
  darkInkShare?: number | null;
  /** Width over height of the artwork, so it can be sized to sit inside the circle. */
  aspect?: number | null;
};

/** What every logo is assumed to be until its pixels say otherwise. */
const EMPTY: Probe = { tint: null, bleed: false, transparent: false, inkLightness: null, darkInkShare: null, aspect: null };

/**
 * One sample per logo — EVER, not per session. A /public-objects key never
 * changes its bytes (a new upload is a new key), so what its pixels said once
 * is what they will always say. Kept in localStorage because the in-memory map
 * alone was empty on every cold launch of the installed app: each logo drew
 * with the default padding, then shifted to its measured look once the sample
 * landed — on every launch, which read as the logos reloading (2026-09-29).
 */
// v2 added transparency and ink lightness; v3 the aspect; v4 the dark-ink share.
// Older samples are read again once.
const PROBE_STORE = "salefish:logoProbe:v4";
const PROBE_CACHE = new Map<string, Probe>(readStoredProbes());
function readStoredProbes(): Array<[string, Probe]> {
  try {
    const raw = typeof localStorage === "undefined" ? null : localStorage.getItem(PROBE_STORE);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}
/** In memory: enough for every logo a long session meets, oldest out first. */
export const PROBE_MEMORY_MAX = 500;
/** On the device: newest last, so a platform admin browsing every project stays small. */
const PROBE_STORED_MAX = 300;

/**
 * Keep a sample in memory. A `blob:` or `data:` source is an upload's preview,
 * never asked for again once it is saved, and a `data:` key can be the whole
 * image — so those are sampled each time instead of piling up for the life of
 * the tab.
 */
export function cacheProbe(src: string, probe: Probe): boolean {
  if (src.startsWith("blob:") || src.startsWith("data:")) return false;
  PROBE_CACHE.delete(src); // re-inserted as the newest
  PROBE_CACHE.set(src, probe);
  while (PROBE_CACHE.size > PROBE_MEMORY_MAX) PROBE_CACHE.delete(PROBE_CACHE.keys().next().value!);
  return true;
}

let storeTimer: ReturnType<typeof setTimeout> | null = null;
function rememberProbe(src: string, probe: Probe) {
  // Only stable object paths are worth keeping on the device.
  if (!cacheProbe(src, probe) || !src.startsWith("/")) return;
  // One write for a burst of samples (a list of forty logos landing together),
  // rather than re-serialising the whole map on every one.
  if (storeTimer) return;
  storeTimer = setTimeout(() => {
    storeTimer = null;
    try {
      const entries = [...PROBE_CACHE.entries()].filter(([k]) => k.startsWith("/")).slice(-PROBE_STORED_MAX);
      localStorage.setItem(PROBE_STORE, JSON.stringify(entries));
    } catch {
      /* private mode or full storage — the in-memory map still serves this session */
    }
  }, 1000);
}

const N = 24; // sampling grid; a logo's shape and hue both survive this far down

/**
 * The dominant *brand* hue of a logo, as a tint pair in the same register as
 * `TINTS`.
 *
 * Averaging every pixel returns mud, and most logos are mostly white. So the
 * greys go first — anything under 12% saturation is paper, ink or rule, never
 * the brand colour — and the survivors are binned by hue and weighted by
 * saturation, so a small saturated mark outvotes a large washed-out one.
 *
 * Only the hue is taken. Saturation and lightness are pinned to the palette's
 * own values, so an acid-green logo yields the same depth of green as the
 * hand-picked forest tint rather than something that glows on the page.
 */
function sampleHue(data: Uint8ClampedArray): Tint | null {
  const BINS = 24; // 15° each
  const weight = new Array<number>(BINS).fill(0);
  const hueSum = new Array<number>(BINS).fill(0);

  for (let i = 0; i < data.length; i += 4) {
    const [r, g, b, a] = [data[i] / 255, data[i + 1] / 255, data[i + 2] / 255, data[i + 3] / 255];
    if (a < 0.5) continue;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    if (max === min) continue; // pure grey
    const s = l > 0.5 ? (max - min) / (2 - max - min) : (max - min) / (max + min);
    if (s < 0.12 || l > 0.94 || l < 0.06) continue; // paper, ink, and near-neutrals
    let h: number;
    if (max === r) h = ((g - b) / (max - min)) % 6;
    else if (max === g) h = (b - r) / (max - min) + 2;
    else h = (r - g) / (max - min) + 4;
    h = (h * 60 + 360) % 360;
    const bin = Math.floor(h / (360 / BINS)) % BINS;
    weight[bin] += s;
    hueSum[bin] += h * s;
  }

  let best = -1;
  for (let i = 0; i < BINS; i++) if (weight[i] > (best < 0 ? 0 : weight[best])) best = i;
  // A wholly greyscale logo has no brand hue to match; the name hash keeps its job.
  if (best < 0 || weight[best] <= 0) return null;

  const hue = Math.round(hueSum[best] / weight[best]);
  return { fg: `${hue} 52% 32%`, bg: `${hue} 42% 88%`, dfg: `${hue} 48% 72%`, dbg: `${hue} 28% 22%` };
}

/**
 * Whether the logo is a *tile* — art drawn out to its own edges on a solid
 * colour, the square avatar a brand hands you — rather than a mark drawn to
 * float on white.
 *
 * A tile wants the whole circle: contain-on-white shrinks it to an inset square
 * whose corners the circle then clips, leaving white wedges at the compass
 * points and a red lozenge in the middle. A wordmark wants the opposite, and
 * the two are told apart at the border: a tile's outermost ring is opaque and
 * coloured, a wordmark's is the paper it was drawn on (or nothing at all).
 *
 * Only square art qualifies, because `cover` on anything else crops it: a
 * 4:5 tile loses a fifth of its height. Art that is merely near-square is
 * contained on white instead — letterboxed, never cut.
 */
function isFullBleed(data: Uint8ClampedArray, aspect: number): boolean {
  if (aspect < 0.97 || aspect > 1.03) return false;

  let ring = 0;
  let inked = 0;
  for (let y = 0; y < N; y++) {
    for (let x = 0; x < N; x++) {
      if (x > 0 && x < N - 1 && y > 0 && y < N - 1) continue;
      ring++;
      const i = (y * N + x) * 4;
      const a = data[i + 3] / 255;
      const l = (Math.max(data[i], data[i + 1], data[i + 2]) + Math.min(data[i], data[i + 1], data[i + 2])) / 510;
      if (a >= 0.9 && l <= 0.93) inked++;
    }
  }
  // A rounded-corner tile leaves a few transparent pixels at each corner, so
  // this asks for most of the ring rather than all of it.
  return inked / ring >= 0.9;
}

/**
 * Whether a logo was drawn on NOTHING (a PNG with no background), and how light
 * its ink is. A transparent logo's outer ring is mostly empty; the ink is
 * every pixel that is not.
 */
function readTransparency(data: Uint8ClampedArray): { transparent: boolean; inkLightness: number | null; darkInkShare: number | null } {
  let ring = 0;
  let clearRing = 0;
  let inkSum = 0;
  let inkCount = 0;
  // Coverage-weighted, and down to a 10% alpha: at 24px a wordmark's thin
  // strokes are almost all anti-aliased edge, under the 50% the average uses,
  // so the average never sees the text at all. (getImageData is not
  // premultiplied, so a faint edge pixel still carries its stroke's colour.)
  let coverage = 0;
  let darkCoverage = 0;
  for (let y = 0; y < N; y++) {
    for (let x = 0; x < N; x++) {
      const i = (y * N + x) * 4;
      const a = data[i + 3] / 255;
      const onRing = x === 0 || y === 0 || x === N - 1 || y === N - 1;
      if (onRing) {
        ring++;
        if (a < 0.5) clearRing++;
      }
      if (a < 0.1) continue;
      const l = (Math.max(data[i], data[i + 1], data[i + 2]) + Math.min(data[i], data[i + 1], data[i + 2])) / 510;
      coverage += a;
      if (l <= DARK_INK) darkCoverage += a;
      if (a >= 0.5) {
        inkSum += l;
        inkCount++;
      }
    }
  }
  return {
    transparent: clearRing / ring >= 0.5,
    inkLightness: inkCount ? inkSum / inkCount : null,
    darkInkShare: coverage ? darkCoverage / coverage : null,
  };
}

/** Ink this dark or darker is lost on the dark plate (the page's own near-black). */
const DARK_INK = 0.35;
/**
 * How much of a logo's ink must be dark for it to need the white plate. A
 * two-tone mark — a blue disc beside a charcoal wordmark — AVERAGES to a mid
 * lightness and used to get the dark plate at night, where the wordmark
 * vanished and the logo read as a lone blue dot (design review, 2026-10-02:
 * the header disc, the sidebar disc, every project card). The harness's
 * logo is 21% dark ink by coverage; a single-colour mid-tone mark is 0%.
 */
const DARK_INK_SHARE = 0.15;

/**
 * The plate a logo sits on, as a class setting `--logo-plate` (Andrew,
 * 2026-09-30: "if the project and builder logos are uploaded with no
 * background as a png, then show a dark background behind the logo when in
 * dark mode").
 *
 *   drawn on a background (opaque)  white, as always — it brought its own paper
 *   transparent, dark ink           white in both themes: a dark plate would
 *                                   swallow a charcoal wordmark
 *   transparent, light ink          dark in BOTH themes — white-on-nothing is
 *                                   invisible on a white plate either way
 *   transparent, anything between   white by day, the page's own dark at night
 */
function plateClass(probe: Probe): string {
  if (whitePlateAtNight(probe)) return "[--logo-plate:#fff]";
  if ((probe.inkLightness ?? 0) >= 0.72) return "[--logo-plate:hsl(252_16%_11%)]";
  return "[--logo-plate:#fff] dark:[--logo-plate:hsl(var(--background))]";
}

/**
 * Whether the plate is white in dark mode — where the logo is dimmed to sit
 * with the page. Decided on the DARKEST significant ink, not the average:
 * dark on average, or a meaningful share of dark ink (a charcoal wordmark
 * beside a coloured mark) — unless the logo is mostly white ink, which a
 * white plate would lose instead.
 */
function whitePlateAtNight(probe: Probe): boolean {
  if (!probe.transparent || probe.inkLightness == null) return true;
  if (probe.inkLightness <= 0.3) return true;
  if (probe.inkLightness >= 0.72) return false;
  return (probe.darkInkShare ?? 0) >= DARK_INK_SHARE;
}

/**
 * The box a contained logo is drawn in, as fractions of the disc (Andrew,
 * 2026-09-30: "the logos are touching the edges of the circle … add a bit of
 * padding so they look intentionally designed").
 *
 * A flat 10% padding left a square box of 80%, whose corners (a diagonal of
 * 113%) the circle cut straight through — a square mark or the ends of a tall
 * wordmark ran into the rim. Instead the artwork's own rectangle is inscribed
 * in a circle 84% of the disc, so its corners clear the rim by 8% whatever its
 * shape, and no side is wider than 76% so a long wordmark still has air at the
 * ends. Before the first sample lands (never again once stored) it is a square.
 */
function logoBox(aspect: number | null | undefined): { w: number; h: number } {
  if (!aspect || !Number.isFinite(aspect)) return { w: 0.6, h: 0.6 };
  const d = Math.hypot(aspect, 1);
  let w = (0.84 * aspect) / d;
  let h = 0.84 / d;
  const over = Math.max(w, h) / 0.76;
  if (over > 1) {
    w /= over;
    h /= over;
  }
  return { w, h };
}

/** Exported for tests/logoPlate.test.ts. */
export const __logoPlateForTests = { readTransparency, plateClass, whitePlateAtNight, logoBox, N, probeCache: PROBE_CACHE };

/**
 * One read of the logo's pixels, shared by every fact that needs them.
 * Resolves to nulls while loading and on any failure.
 */
function useLogoProbe(src: string | null | undefined): Probe {
  const [probe, setProbe] = useState<Probe>(() => (src ? (PROBE_CACHE.get(src) ?? EMPTY) : EMPTY));

  useEffect(() => {
    if (!src) return setProbe(EMPTY);
    const cached = PROBE_CACHE.get(src);
    if (cached) return setProbe(cached);

    let live = true;
    const img = new Image();
    img.crossOrigin = "anonymous";
    // The 64px copy is all a 24×24 sample needs, not the full-size original.
    const sampleSrc = imageVariant(src, N, 2);
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = canvas.height = N;
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      let read: Probe = EMPTY;
      if (ctx) {
        ctx.drawImage(img, 0, 0, N, N);
        // Reading pixels off a canvas fed by another origin throws unless the
        // server sent CORS headers and the image was requested with crossOrigin.
        try {
          const { data } = ctx.getImageData(0, 0, N, N);
          read = {
            tint: sampleHue(data),
            bleed: isFullBleed(data, img.naturalWidth / img.naturalHeight),
            ...readTransparency(data),
            aspect: img.naturalHeight ? img.naturalWidth / img.naturalHeight : null,
          };
        } catch {
          /* cross-origin without CORS — fall through to the safe defaults */
        }
      }
      rememberProbe(src, read);
      if (live) setProbe(read);
    };
    // A logo that will not load is not worth retrying on every render.
    img.onerror = () => {
      cacheProbe(src, EMPTY);
      if (live) setProbe(EMPTY);
    };
    img.src = sampleSrc;
    return () => {
      live = false;
    };
  }, [src]);

  return probe;
}

/**
 * Unified letter-avatar fallback (spec §10.2): circle, system font, bold
 * initials, deterministic deep-jewel tint from the name hash. Circular
 * everywhere — no squircles.
 *
 * `mode` decides what a project logo is used for:
 *
 *   "logo"     — draw it. `contain` on white, inset (`logoBox`), because a logo is not
 *                a photograph: it is drawn to sit on white with air around it,
 *                and its wordmark means nothing once the sides are cropped off.
 *                A logo that is instead a full-bleed tile (`isFullBleed`) is the
 *                exception and fills the circle — it brought its own background
 *                and its own margin, and insetting it on white only stacks a
 *                second margin on the first. Pass `fit="cover"` for anything
 *                photographic.
 *   "initials" — draw initials, but take their colour from the logo. Below about
 *                32px a wordmark is a smudge, so the small avatars show letters —
 *                tinted to the brand so a row still reads as that project rather
 *                than as whatever colour its name happened to hash to.
 *
 * DISPLAY: the disc is an `inline-grid` and its centring (`place-items-center`)
 * only works in a grid. To hide it at a breakpoint pass `hidden sm:inline-grid`
 * — a `sm:flex` overrides the grid and the initials sit off-centre, which is
 * what the Sales List shipped on 2026-09-30. tests/letterAvatarDisplay.test.ts.
 *
 * `flat` drops the elevation, for avatars in a table. A shadow under one avatar
 * lifts it off the page; the same shadow repeated down every row of a column
 * reads as texture on the table instead, which is the opposite of what
 * elevation is for.
 */
export function LetterAvatar({
  name,
  src,
  size = 40,
  fit = "contain",
  mode = "logo",
  flat = false,
  priority = false,
  className,
}: {
  name: string;
  /** A LOGO. People have no picture — pass nothing for a person. */
  src?: string | null;
  size?: number;
  fit?: "contain" | "cover";
  /** `initials` shows letters even when a logo exists, tinted from the logo. */
  mode?: "logo" | "initials";
  /** No drop shadow — for a disc sitting inside a list row or a tile. */
  flat?: boolean;
  /**
   * Always on screen (the page header, the sidebar, the picker's first
   * cards): load at once. `lazy` waits for a layout pass before it asks for
   * even a cached file, so the mark drew its plate first and the logo a beat
   * later — on every page.
   */
  priority?: boolean;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  const asInitials = mode === "initials" || !src || failed;
  const probe = useLogoProbe(src);
  const tint = (mode === "initials" || failed ? probe.tint : null) ?? TINTS[hashString(name || "?") % TINTS.length];
  const cover = fit === "cover" || probe.bleed;
  const box = logoBox(probe.aspect);

  // `grid-cols-1 grid-rows-1` pins the one cell to the disc's content box. With
  // the implicit `auto` tracks, the <img>'s `height: 100%` has nothing definite
  // to resolve against, so the row grew to the art's own proportions — a
  // 304×433 logo drew 42×59 in a 42px box and the circle cut off its top and
  // bottom, `object-contain` notwithstanding. Same in WebKit and Chromium.
  return (
    <span
      className={cn(
        "inline-grid shrink-0 grid-cols-1 grid-rows-1 place-items-center overflow-hidden rounded-full font-bold leading-none",
        !flat && "shadow-[0_1px_2px_hsl(var(--scrim)/0.22),0_8px_20px_-5px_hsl(var(--scrim)/0.45)]",
        "ring-1 ring-inset ring-black/5 dark:ring-white/[.06]",
        asInitials && "bg-[hsl(var(--av-bg))] dark:bg-[hsl(var(--av-dbg))]",
        !asInitials && !cover && plateClass(probe),
        // A logo on white is dimmed at night to sit with the page; one already
        // on a dark plate is not — dimming it there only loses contrast.
        !asInitials && (cover || whitePlateAtNight(probe)) && "dark:brightness-[.86]",
        "text-[hsl(var(--av-fg))] dark:text-[hsl(var(--av-dfg))]",
        className,
      )}
      style={{
        width: `var(--letter-avatar-size, ${size}px)`,
        height: `var(--letter-avatar-size, ${size}px)`,
        fontSize: `calc(var(--letter-avatar-size, ${size}px) * .36)`,
        letterSpacing: "-.02em",
        background: asInitials || cover ? undefined : "var(--logo-plate, #fff)",
        ["--av-fg" as string]: tint.fg,
        ["--av-bg" as string]: tint.bg,
        ["--av-dfg" as string]: tint.dfg,
        ["--av-dbg" as string]: tint.dbg,
        // No transition: the look is settled from the stored sample on every
        // launch after the first, and animating the first one's arrival (size
        // and background sliding over 200ms) was the visible "reload".
      }}
      aria-hidden={asInitials ? true : undefined}
    >
      {asInitials ? (
        initials(name)
      ) : (
        <img
          // The same file, in the same CORS mode, that lib/projectImages.ts warms.
          {...logoImage(src!, size)}
          alt=""
          // An avatar in a long list (contacts, team, activity) off screen
          // waits until it is scrolled near, rather than all downloading on
          // first paint — unless it is one that is always on screen.
          loading={priority ? "eager" : "lazy"}
          decoding={priority ? "auto" : "async"}
          onError={() => setFailed(true)}
          className={cover ? "size-full object-cover" : "object-contain"}
          style={cover ? undefined : { width: `${box.w * 100}%`, height: `${box.h * 100}%` }}
        />
      )}
    </span>
  );
}
